import {
  MinusCircleOutlined,
  HistoryOutlined,
  SwapOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  ShopOutlined,
  AppstoreOutlined,
  PercentageOutlined,
  TagOutlined,
  TeamOutlined,
  DashboardOutlined,
  DesktopOutlined,
  FieldTimeOutlined,
  FundOutlined,
  LeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CreditCardOutlined,
  BlockOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../store/hooks";
import { useAssortmentReportTitleKey } from "../useAssortmentReportTitleKey";
import { useReportSectionsVisibility } from "./useReportSectionsVisibility";

type Props = {
  collapsed: boolean;
  onToggle: () => void;
  selectedKey?: string;
  onMenuClick?: (key: string) => void;
  isNarrow?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

type SidebarItem = {
  key: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  permission: string;
};

type SidebarSection = {
  title: string | null;
  items: SidebarItem[];
  hidden?: boolean;
};

export default function Sidebar({
  collapsed,
  onToggle,
  selectedKey = "0",
  onMenuClick,
  isNarrow = false,
  mobileOpen = false,
  onMobileClose,
}: Props) {
  // На мобилно sidebar-ът винаги е разгънат (не показваме collapse toggle)
  const effectiveCollapsed = isNarrow ? false : collapsed;
  const { t } = useTranslation();
  const navigate = useNavigate();

  // `access` тук е само за цвета на марката — потвърденият втори код се
  // вижда по деколорирания надпис, не по различен списък справки.
  const access = useAppSelector((s) => s.auth.access);
  const permissions = useAppSelector((s) => s.auth.permissions);
  const hasPerm = (name: string) => permissions.some((p) => p.name === name);
  // В режим „със склад" справката се казва „Справка по рецепти".
  const assortmentTitleKey = useAssortmentReportTitleKey();
  // Резервации без маси в схемата и Фискални без фирма просто не се показват.
  const { showReservations, showFiscal } = useReportSectionsVisibility();

  // Секциите следват РАБОТНИЯ ПОТОК, не техническото деление:
  //   Контрол   — какво се е случило по масите и какво иска проверка;
  //   Продажби  — какво е продадено и от кого;
  //   Резервации — кой е резервирал, кога идват гостите, кой не се явява;
  //   Фискални  — документите (фактури, картови тегления);
  //   Отчети    — дневните отчети, които се печатат.
  const rawSections: SidebarSection[] = [
    {
      title: null,
      items: [
        {
          key: "0",
          label: t("reportsPage.dashboard"),
          Icon: FundOutlined,
          permission: "view_dashboard",
        },
      ],
    },
    {
      title: t("reportsPage.tableReports"),
      items: [
        {
          key: "1",
          label: t("reportsPage.billsHistory"),
          Icon: HistoryOutlined,
          permission: "view_tables_history",
        },
        {
          key: "4",
          label: t("reportsPage.cancellations"),
          Icon: MinusCircleOutlined,
          permission: "view_voids",
        },
        {
          key: "7",
          label: t("reportsPage.clientsReport"),
          Icon: PercentageOutlined,
          permission: "view_discounts_report",
        },
        {
          key: "2",
          label: t("reportsPage.billsSplits"),
          Icon: SwapOutlined,
          permission: "view_split_tables",
        },
        {
          key: "12",
          label: t("reportsPage.priceChanges"),
          Icon: TagOutlined,
          permission: "view_price_changes_report",
        },
        {
          key: "13",
          label: t("reportsPage.kitchenMonitors"),
          Icon: DesktopOutlined,
          permission: "view_kitchen_monitors_report",
        },
      ],
    },
    {
      title: t("reportsPage.generalReports"),
      items: [
        {
          key: "8",
          label: t("reportsPage.waitersReport"),
          Icon: TeamOutlined,
          permission: "view_waiters_report",
        },
        {
          key: "3",
          label: t(assortmentTitleKey),
          Icon: AppstoreOutlined,
          permission: "view_assortments_report",
        },
        {
          key: "15",
          label: t("reportsPage.categoriesReport"),
          Icon: BlockOutlined,
          permission: "view_assortments_report",
        },
        {
          key: "9",
          label: t("reportsPage.stationsReport"),
          Icon: DashboardOutlined,
          permission: "view_sites_report",
        },
        {
          key: "16",
          label: t("reportsPage.salesPeriodsReport"),
          Icon: FieldTimeOutlined,
          permission: "view_sales_by_periods_report",
        },
      ],
    },
    {
      title: t("reportsPage.reservationsReports"),
      hidden: !showReservations,
      items: [
        {
          key: "17",
          label: t("reportsPage.rvJournal"),
          Icon: CalendarOutlined,
          permission: "view_reservations_journal_report",
        },
        {
          key: "18",
          label: t("reportsPage.rvOccupancy"),
          Icon: ClockCircleOutlined,
          permission: "view_reservations_occupancy_report",
        },
        {
          key: "19",
          label: t("reportsPage.rvGuests"),
          Icon: ContactsOutlined,
          permission: "view_reservations_guests_report",
        },
      ],
    },
    {
      title: t("reportsPage.fiscalReports"),
      hidden: !showFiscal,
      items: [
        {
          key: "10",
          label: t("reportsPage.invoices"),
          Icon: FileDoneOutlined,
          permission: "view_invoices",
        },
        {
          key: "14",
          label: t("reportsPage.cardPayments"),
          Icon: CreditCardOutlined,
          permission: "view_tables_history",
        },
      ],
    },
    {
      // Дневните отчети са само по право — без втори код при архивиран
      // ресторант BE-то ги връща преизчислени без закритите в брой без бон
      // (виж Reports.tsx), затова секцията не се крие.
      title: t("reportsPage.reportsManagement"),
      items: [
        {
          key: "5",
          label: t("reportsPage.personalReports"),
          Icon: FileTextOutlined,
          permission: "view_personal_reports",
        },
        {
          key: "6",
          label: t("reportsPage.establishmentReports"),
          Icon: ShopOutlined,
          permission: "view_establishment_reports",
        },
      ],
    },
  ];

  // Допълнителен permission филтър върху всяка секция; празните секции отпадат.
  const sections: SidebarSection[] = rawSections
    .filter((sec) => !sec.hidden)
    .map((sec) => ({
      ...sec,
      items: sec.items.filter((it) => hasPerm(it.permission)),
    }))
    .filter((sec) => sec.items.length > 0);

  if (isNarrow && !mobileOpen) {
    return null;
  }

  return (
    <aside
      className={`flex flex-col flex-shrink-0${isNarrow ? " dk-elevated" : ""}`}
      style={{
        width: isNarrow ? 280 : effectiveCollapsed ? 68 : 248,
        transition: isNarrow ? "none" : "width 180ms ease",
        background: "var(--color-sidebar)",
        borderRight: "1px solid var(--color-shade-2)",
        padding: "16px 12px",
        fontFamily:
          '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        color: "var(--color-body)",
        position: isNarrow ? "fixed" : "relative",
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: isNarrow ? 50 : "auto",
        boxShadow: isNarrow ? "0 10px 30px rgba(17,17,17,0.2)" : "none",
      }}
    >
      {/* Back link / Close drawer (on mobile) */}
      {isNarrow ? (
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: 14 }}
        >
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 rounded-md text-left"
            style={{
              padding: "6px 10px",
              color: "var(--color-body)",
              fontSize: 12,
              fontWeight: 500,
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            <LeftOutlined style={{ fontSize: 11 }} />
            {t("reportsPage.dashboardBackToModules")}
          </button>
          <button
            onClick={onMobileClose}
            aria-label="Close menu"
            style={{
              padding: 6,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--color-body)",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      ) : effectiveCollapsed ? (
        <Tooltip
          title={t("reportsPage.dashboardBackToModules")}
          placement="right"
        >
          <button
            onClick={() => navigate("/")}
            aria-label={t("reportsPage.dashboardBackToModules")}
            className="flex items-center justify-center"
            style={{
              padding: "6px 0",
              marginBottom: 14,
              color: "var(--color-body)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            <LeftOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      ) : (
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 rounded-md text-left"
          style={{
            padding: "6px 10px",
            marginBottom: 14,
            color: "var(--color-body)",
            fontSize: 12,
            fontWeight: 500,
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          <LeftOutlined style={{ fontSize: 11 }} />
          {t("reportsPage.dashboardBackToModules")}
        </button>
      )}

      {/* Brand */}
      <div
        className="flex items-center"
        style={{
          gap: 10,
          padding: effectiveCollapsed ? "4px 0 16px" : "4px 10px 16px",
          borderBottom: "1px solid var(--color-shade-2)",
          marginBottom: 14,
          justifyContent: effectiveCollapsed ? "center" : "flex-start",
        }}
      >
        <img
          src="/logo-ts.png"
          alt="UnrealSoft"
          style={{ width: 32, height: 32, objectFit: "contain" }}
        />
        {!effectiveCollapsed && (
          <div style={{ lineHeight: 1.2, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: access ? "var(--color-brand-deep-ink)" : "var(--color-ink)",
                letterSpacing: -0.1,
              }}
            >
              UnrealSoft
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--color-muted)",
                marginTop: 2,
                whiteSpace: "nowrap",
              }}
            >
              {t("reportsPage.dashboardModuleLabel")}
            </div>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto">
        {sections.map((sec, si) => (
          <div key={si} style={{ marginBottom: 16 }}>
            {sec.title && !effectiveCollapsed && (
              <div
                style={{
                  padding: "0 10px 6px",
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: 1.4,
                  color: "var(--color-muted)",
                  textTransform: "uppercase",
                }}
              >
                {sec.title}
              </div>
            )}
            {sec.title && effectiveCollapsed && si > 0 && (
              <div
                style={{
                  margin: "0 8px 8px",
                  height: 1,
                  background: "var(--color-shade-2)",
                }}
              />
            )}
            {sec.items.map((it) => (
              <SidebarLink
                key={it.key}
                item={it}
                active={selectedKey === it.key}
                collapsed={effectiveCollapsed}
                onClick={() => onMenuClick?.(it.key)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Footer: collapse toggle */}
      {!isNarrow && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 10,
            borderTop: "1px solid var(--color-shade-2)",
          }}
        >
          <Tooltip
            title={
              effectiveCollapsed
                ? t("reportsPage.expandMenu")
                : t("reportsPage.collapseMenu")
            }
            placement="right"
          >
            <button
              onClick={onToggle}
              aria-label={
                effectiveCollapsed
                  ? t("reportsPage.expandMenu")
                  : t("reportsPage.collapseMenu")
              }
              className="flex items-center"
              style={{
                width: "100%",
                gap: 10,
                padding: effectiveCollapsed ? "9px 0" : "9px 10px",
                justifyContent: effectiveCollapsed ? "center" : "flex-start",
                borderRadius: 8,
                background: "transparent",
                border: "none",
                color: "var(--color-muted)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                transition: "background 120ms, color 120ms",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-shade-1)";
                e.currentTarget.style.color = "var(--color-ink)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--color-muted)";
              }}
            >
              {effectiveCollapsed ? (
                <MenuUnfoldOutlined style={{ fontSize: 15 }} />
              ) : (
                <>
                  <MenuFoldOutlined style={{ fontSize: 15 }} />
                  <span>{t("reportsPage.collapse")}</span>
                </>
              )}
            </button>
          </Tooltip>
        </div>
      )}
    </aside>
  );
}

function SidebarLink({
  item,
  active,
  collapsed,
  onClick,
}: {
  item: SidebarItem;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const { Icon, label } = item;
  const button = (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: collapsed ? "9px 0" : "8px 10px",
        justifyContent: collapsed ? "center" : "flex-start",
        borderRadius: 7,
        background: active ? "var(--color-surface)" : "transparent",
        color: active ? "var(--color-ink)" : "var(--color-body)",
        fontSize: 12.5,
        fontWeight: active ? 600 : 500,
        textAlign: "left",
        width: "100%",
        border: "none",
        cursor: "pointer",
        position: "relative",
        transition: "background 120ms, color 120ms",
        boxShadow: active
          ? "0 1px 2px rgba(17,17,17,0.04), 0 0 0 1px rgba(17,17,17,0.05)"
          : "none",
        marginBottom: 2,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "var(--color-shade-1)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      {active && !collapsed && (
        <span
          style={{
            position: "absolute",
            left: -12,
            top: 7,
            bottom: 7,
            width: 3,
            background: "var(--color-brand)",
            borderRadius: "0 3px 3px 0",
          }}
        />
      )}
      <Icon className="text-[14px]" />
      {!collapsed && (
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
      )}
    </button>
  );
  if (collapsed) {
    return (
      <Tooltip title={label} placement="right">
        {button}
      </Tooltip>
    );
  }
  return button;
}
